import React, { useState } from "react";

import { HelpIcon, Typography } from "@packages/ds";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { AddButton, Link as LinkButton, CloseButton } from "ds/Button";
import * as Dropdown from "ds/Dropdown";
import Loading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import * as Tabs from "ds/Tabs";
import { Tooltip } from "ds/Tooltip";
import { useLocalForage } from "Hooks/useLocalForage";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { useLoadInvitationsAndAccesses } from "Reducers/access/access.hooks";
import { selectAccessesForView } from "Reducers/access/environmentTypeAccess/environmentTypeAccess.selectors";
import { selectProjectInvitations } from "Reducers/access/invitation/invitation.selectors";
import {
  useGetProjectTeamAccesses,
  projectAccessTeamCountSelector
} from "Reducers/access/teams/teams.slice";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import {
  selectHasManagementAccess,
  gitProjectSelector
} from "Reducers/project/project";
import { userAccessCountByProjectIdSelector } from "Reducers/userAccess/userAccess.selectors";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useIsTeamsEnabled } from "src/organization/pages/settings/pages/teams/hooks/useIsTeamsEnabled";
import {
  TabContent,
  TabRoot
} from "src/organization/pages/settings/pages/teams/TeamView.styles";
import { useAppSelector } from "Store/hooks";

import { LeaveProjectModal } from "../Common/LeaveProjectModal";

import { AccessTable } from "./AccessTable";
import { AccessTableNoOrg } from "./AccessTableNoOrg";
import { AddProjectToTeamModal } from "./AddProjectToTeamModal";
import * as S from "./ProjectAccessList.styles";
import { ProjectAccessTeamsList } from "./ProjectAccessTeamsList";

type TabstriggersProps = {
  projectId: string;
};

const TabsTriggers = (props: TabstriggersProps) => {
  const accessCount = useSelectorWithUrlParams(
    userAccessCountByProjectIdSelector
  );
  const teamsCount = useSelectorWithUrlParams(projectAccessTeamCountSelector);

  // We need to load the project teams here otherwise the count won't be populated
  // until the user selects the tab.
  useGetProjectTeamAccesses({
    projectId: props.projectId
  });

  return (
    <Tabs.List>
      <Tabs.Trigger value="access">
        <FormattedMessage id="tabs.users" /> ({accessCount})
      </Tabs.Trigger>
      <Tabs.Trigger value="teams">
        <FormattedMessage id="tabs.teams" /> ({teamsCount})
      </Tabs.Trigger>
    </Tabs.List>
  );
};

const ProjectAccessList = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [selectedUserToRemove, setSelectedUserToRemove] = useState<{
    id: string;
    name: string;
  }>();
  const isTeamsEnabled = useIsTeamsEnabled();
  const organization = useOrganizationFromURL();
  const { value: hideAddTeamsCTA = false, setValue: setHideAddTeamsCTA } =
    useLocalForage("add_teams_cta_sticker");
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);

  const { projectId } = useParams();

  const invitations = useAppSelector(
    state => projectId && selectProjectInvitations(state, { projectId })
  );
  const [isAddProjectToTeamModalOpen, setIsAddProjectToTeamModalOpen] =
    useState(false);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const users = useSelectorWithUrlParams(selectAccessesForView);
  const loading = useLoadInvitationsAndAccesses();

  const loggedInUserId = useAppSelector(selectCurrentUserUUID);

  const manageAccessEnabled = useSelectorWithUrlParams(
    selectHasManagementAccess
  );

  if (loading)
    return (
      <div>
        <Loading />
      </div>
    );

  return (
    <>
      <S.Header>
        <Typography.Title tag="h3">
          {intl.formatMessage({ id: "access.title" })}
        </Typography.Title>
        {manageAccessEnabled ? (
          isTeamsEnabled === "enabled" ? (
            <Dropdown.Root id="project_access_list">
              <DropdownMenu.Trigger asChild>
                <AddButton analyticId="invitation.add">
                  <FormattedMessage id="invitation.add" />
                </AddButton>
              </DropdownMenu.Trigger>
              <Dropdown.Content align="end">
                <Dropdown.LinkItem to="new">
                  <FormattedMessage id="project.accesslist.add_user" />
                </Dropdown.LinkItem>
                <Dropdown.Item
                  onClick={() => setIsAddProjectToTeamModalOpen(true)}
                >
                  <FormattedMessage id="project.accesslist.add_team" />
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Root>
          ) : (
            <AddButton to="new" analyticId="invitation.add">
              {intl.formatMessage({ id: "invitation.add" })}
            </AddButton>
          )
        ) : (
          <Tooltip
            align="end"
            tooltip={intl.formatMessage({
              id: "project.accesslist.addbyadmin"
            })}
            analyticId="project.accesslist.addbyadmin"
          >
            <AddButton to="new" disabled={true} analyticId="invitation.add">
              {intl.formatMessage({ id: "invitation.add" })}
            </AddButton>
          </Tooltip>
        )}
      </S.Header>
      <S.Info>
        <Typography.BodyText>
          {intl.formatMessage({ id: "project.accesslist.description" })}
        </Typography.BodyText>
        <S.Help>
          <HelpIcon />
          {intl.formatMessage({ id: "access.help.text.a" })}{" "}
          <LinkButton
            href={intl.formatMessage({
              id: "links.documentation.access"
            })}
            analyticId="access.help.text.b"
          >
            <span>{intl.formatMessage({ id: "access.help.text.b" })}</span>
          </LinkButton>
          {"."}
        </S.Help>
      </S.Info>
      {isTeamsEnabled === "disabled-promote" && !hideAddTeamsCTA && (
        <S.StickerWrapper priority="information">
          <Sticker.Icon priority="information" />
          <Sticker.Text>
            <FormattedMessage
              id="teams.cta.upgrade.plan"
              values={{
                learnmore: (
                  <LinkButton
                    analyticId="access.help.text.b"
                    href={intl.formatMessage({
                      id: "links.documentation.access"
                    })}
                  >
                    {intl.formatMessage({
                      id: "learnmore"
                    })}
                  </LinkButton>
                )
              }}
            />
          </Sticker.Text>
          <LinkButton
            variant="primary"
            href="https://platform.sh/contact"
            analyticId="contact_sales"
          >
            {intl.formatMessage({
              id: "contact_sales"
            })}
          </LinkButton>
          <CloseButton onClick={() => setHideAddTeamsCTA(true)} />
        </S.StickerWrapper>
      )}
      {isTeamsEnabled === "enabled" && invitations && projectId ? (
        <>
          <TabRoot defaultValue="access">
            {projectId && <TabsTriggers projectId={projectId} />}
            <TabContent value="access">
              {!organizationEnabled ? (
                <AccessTable invitations={invitations} users={users} />
              ) : (
                <AccessTableNoOrg
                  currentLoggedInMemberId={loggedInUserId || ""}
                  onSelectUserToRemove={setSelectedUserToRemove}
                  projectOwnerId={project?.owner || ""}
                />
              )}
            </TabContent>
            <TabContent value="teams">
              <ProjectAccessTeamsList
                onAdd={() => setIsAddProjectToTeamModalOpen(true)}
                projectId={projectId}
              />
            </TabContent>
          </TabRoot>

          {isAddProjectToTeamModalOpen && organization && (
            <AddProjectToTeamModal
              onClose={() => setIsAddProjectToTeamModalOpen(false)}
              projectId={projectId}
              organization={organization}
            />
          )}
        </>
      ) : invitations ? (
        !organizationEnabled ? (
          <AccessTable invitations={invitations} users={users} />
        ) : (
          <AccessTableNoOrg
            currentLoggedInMemberId={loggedInUserId || ""}
            onSelectUserToRemove={setSelectedUserToRemove}
            projectOwnerId={project?.owner || ""}
          />
        )
      ) : null}

      {organization && projectId && selectedUserToRemove && (
        <LeaveProjectModal
          organizationId={organization.id}
          onSuccess={() => navigate(`/${organization.name}`)}
          projectId={projectId}
          projectName={project?.title || ""}
          userId={selectedUserToRemove.id}
          userName={
            selectedUserToRemove.id === loggedInUserId
              ? intl.formatMessage({ id: "project.access.yourself" })
              : selectedUserToRemove.name
          }
          fullUserName={selectedUserToRemove.name}
          onClose={() => {
            setSelectedUserToRemove(undefined);
          }}
        />
      )}
    </>
  );
};

export default ProjectAccessList;
